import {
	Paper,
	Box,
	FormControlLabel,
	Switch,
	useMediaQuery
} from "@mui/material";
import LogoSpinner from '../../../components/LogoSpinner/LogoSpinner';
import { GeoJSON, MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import React, { useEffect, useState } from "react";
import il_sinirlari from "../../../assets/geojson/tr-cities-utf8.json";
import * as L from "leaflet";
import { LatLngExpression } from "leaflet";
import { addFeatureEvents, getFeatureStyle } from "../../../helpers/mapStyles";
import { markerClusterPieChart } from "../../../helpers/markerClusterPieChart";
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import { useSelector } from "../../../redux/store";
import WikipediaModal from "./wikipediaModal";
import { fetchWikipediaData, fetchWikipediaDataByCoordinates } from "../../../api/publicApi/wikipedia";
import Legend from './Legend';
import DistrictModal from './DistrictModal';
import EventPinModal, { EventItem } from './EventPinModal';
import ReportPinModal from "./ReportPinModal";
import ChecklistModal from './checklistModal';
import EventsApi from "../../../api/eventsApi/eventsApi";
import 'leaflet.heat';
import CacheService from '../../../services/CacheService';

interface IMapProps {
	events: any,
	filters: any,
	setFilters: any,
	pins: any,
	setPins: any,
	isLoading: boolean
}

interface CityEventCount {
	city: string;
	count: number;
	averageDailyCount: number;
	lastDayCount: number;
	logThreatLevel: number;
	threatLevel: number;
	events?: Array<{
		latitude: number;
		longitude: number;
		eventTypeId: string;
		description: string;
		date: string;
	}>;
}

interface DistrictEventCount {
	district: string;
	count: number;
	averageDailyCount: number;
	lastDayCount: number;
	logThreatLevel: number;
	threatLevel: number;
	events?: Array<{
		latitude: number;
		longitude: number;
		eventTypeId: string;
		description: string;
		date: string;
	}>;
}

interface PinResponse {
	items: Array<{
		id: string;
		name: string;
		latitude: number;
		longitude: number;
		radiusInMeters: number;
		sector: string;
		userId: string;
	}>;
}

const customMarkerIcon = new L.Icon({
	iconUrl: '/placeholder.png', //????
	iconSize: [32, 32], // İkon boyutu: genişlik, yükseklik
	iconAnchor: [16, 32], // Haritada sabit noktası (ikonun alt ortası)
	popupAnchor: [0, -32], // Popup konumlandırması
});

// Cache anahtarları
const MAP_CACHE_KEYS = {
	PINS: 'map_pins',
	PIN_INFO: (pinId: string) => `map_pin_info_${pinId}`,
	EVENTS_COUNT: (city: string, dateStart: string, dateEnd: string, eventTypeId: string = '') =>
		`map_events_count_${city || 'all'}_${dateStart}_${dateEnd}_${eventTypeId || 'all'}`
};

// Cache süresi
const MAP_CACHE_EXPIRY = {
	SHORT: 5 * 60 * 1000,  // 5 dakika
	MEDIUM: 30 * 60 * 1000 // 30 dakika
};

const Map = (props: IMapProps) => {
	const { events, filters, setFilters, pins, setPins, isLoading } = props;
	const center: LatLngExpression = [38.9637, 35.2433];
	const [useNewColorScale, setUseNewColorScale] = useState<boolean>(true);

	// Responsive breakpoints
	const isTabletScreen = useMediaQuery('(min-width:600px) and (max-width:1199px)');

	return (
		<Paper elevation={3} sx={{
			width: '100%',
			height: isTabletScreen ? '40vh' : '75vh', // Tablet'te daha da düşük yükseklik
			overflow: 'hidden',
			position: 'relative',
			zIndex: 1,
			backgroundColor: '#f5f5f5',
			maxWidth: '100%', // Genişliği sınırla
			boxSizing: 'border-box' // Padding ve border'ı genişliğe dahil et
		}}>
			{isLoading && (
				<Box
					sx={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						backgroundColor: 'rgba(255, 255, 255, 0.7)',
						zIndex: 1000,
					}}
				>
					<LogoSpinner size={48} minHeight="auto" />
				</Box>
			)}
			<MapContainer
				center={center}
				zoom={isTabletScreen ? 6 : 6} // Tablet'te zoom seviyesini düzelttim
				style={{
					height: '100%',
					width: '100%',
					backgroundColor: '#f5f5f5',
					zIndex: 1
				}}
				dragging={true}
				touchZoom={true}
				doubleClickZoom={true}
				scrollWheelZoom={true}
				boxZoom={true}
				keyboard={true}
				zoomControl={true}
			>
				<MapComponents
					events={events}
					filters={filters}
					setFilters={setFilters}
					pins={pins}
					setPins={setPins}
					isLoading={isLoading}
					useNewColorScale={useNewColorScale}
					setUseNewColorScale={setUseNewColorScale}
				/>
			</MapContainer>
		</Paper>
	)
}

// HeatLayer için type declaration ekleyelim
declare module 'leaflet' {
	export interface HeatMapOptions {
		minOpacity?: number;
		maxZoom?: number;
		max?: number;
		radius?: number;
		blur?: number;
		gradient?: { [key: number]: string };
	}

	export interface HeatLayer extends L.Layer {
		setLatLngs(latlngs: L.LatLngExpression[]): void;
		addLatLng(latlng: L.LatLngExpression): void;
		setOptions(options: HeatMapOptions): void;
	}

	export interface HeatLayerFactory {
		(latlngs: L.LatLngExpression[], options?: HeatMapOptions): HeatLayer;
	}

	export interface LeafletStatic {
		heatLayer: HeatLayerFactory;
	}

	interface Map {
		pm?: any;
		addControl(control: Control.Draw): this;
		removeControl(control: Control.Draw): this;
	}
}

const MapComponents = (props: IMapProps & {
	useNewColorScale: boolean;
	setUseNewColorScale: (value: boolean) => void;
}) => {
	const { events, filters, setFilters, isLoading, useNewColorScale, setUseNewColorScale } = props;
	const map = useMap();

	// Responsive breakpoint
	const isTabletScreen = useMediaQuery('(min-width:600px) and (max-width:1199px)');
	const [cityPins, setCityPins] = useState<Array<{
		id: string;
		name: string;
		latitude: number;
		longitude: number;
		radiusInMeters: number;
		sector: string;
		userId: string;
	}>>([]);
	const [searchQuery, setSearchQuery] = React.useState('');
	const [suggestions, setSuggestions] = React.useState<any[]>([]);
	const [searchPin, setSearchPin] = useState<[number, number] | null>(null);
	const [wikipediaModalOpen, setWikipediaModalOpen] = useState(false);
	const [eventPinModalOpen, setEventPinModalOpen] = useState(false);
	const [reportPinModalOpen, setReportPinModalOpen] = useState(false);
	const [selectedPinEvents, setSelectedPinEvents] = useState<EventItem[]>([]);
	const [selectedPinCoordinates, setSelectedPinCoordinates] = useState<{ latitude: number; longitude: number } | null>(null);
	const [districtModalOpen, setDistrictModalOpen] = useState(false);
	const [selectedDistrict, setSelectedDistrict] = useState("");
	const [showPieChart, setShowPieChart] = useState<boolean>(true);
	const [showDistrictFill, setShowDistrictFill] = useState<boolean>(true);
	const [showHeatmap, setShowHeatmap] = useState<boolean>(false);
	const [wikipediaData, setWikipediaData] = useState<{
		title: string | null;
		summary: string | null;
		thumbnail: string | null;
	}>({
		title: null,
		summary: null,
		thumbnail: null
	});
	const [loading, setLoading] = useState<boolean>(false);
	const [savedPinData, setSavedPinData] = useState<{
		id?: string;
		name: string;
		latitude: number;
		longitude: number;
		radius: number;
		sector: string;
		reportDescription?: string | null;
	} | null>(null);
	const [checklistOpen, setChecklistOpen] = useState(false);
	const [drawnItems] = useState(() => new L.FeatureGroup());
	const [drawnCircle, setDrawnCircle] = useState<{
		center: [number, number];
		radius: number;
	} | null>(null);
	const [districtBounds, setDistrictBounds] = useState<any>(null);

	const center: LatLngExpression = [38.9637, 35.2433];

	const getCleanTitle = (displayName: string) => {
		const parts = displayName.split(',');
		return parts[0].trim(); // İlk kısmı al ve döndür
	};

	const { districtBounds: ilce_sinirlari } = useSelector(state => state.app)
	// const drawnItems = L.featureGroup().addTo(map);

	useEffect(() => {
		if (map) {
			if (suggestions.length > 0) {
				map.scrollWheelZoom.disable(); // Dropdown açıkken zoom'u devre dışı bırak
			} else {
				map.scrollWheelZoom.enable(); // Dropdown kapalıyken zoom'u tekrar aktif et
			}
		}
	}, [map, suggestions]);

	const calculateTValue = (count: number): number => {
		return Math.log(count + 1) * 3; // Basit bir risk hesaplaması
	}

	const interpretTValue = (t: number): string => {
		if (t < 3) return 'Düşük';
		if (t >= 3 && t <= 6) return 'Orta';
		if (t >= 6 && t <= 12) return 'Yüksek';
		return 'Çok Yüksek';
	}

	var ilSinirlari: any = il_sinirlari;

	// GeoJSON ("Istanbul") ile DB ("İstanbul","Hakkâri","Çanakkale" vb.) eşleşmesi
	// Türkçe özel + aksan + circumflex karakterlerin tamamını normalize eder
	const normalizeCity = (name: string): string => {
		if (!name) return '';
		return name
			// Türkçe özel karakterler
			.replace(/İ/g, 'I').replace(/ı/g, 'i')
			.replace(/Ş/g, 'S').replace(/ş/g, 's')
			.replace(/Ğ/g, 'G').replace(/ğ/g, 'g')
			.replace(/Ü|Û/g, 'U').replace(/ü|û/g, 'u')
			.replace(/Ö/g, 'O').replace(/ö/g, 'o')
			.replace(/Ç/g, 'C').replace(/ç/g, 'c')
			// Circumflex / düzeltme işareti: â→a, î→i, ê→e, ô→o, û→u (Hakkâri vb.)
			.replace(/Â/g, 'A').replace(/â/g, 'a')
			.replace(/Î/g, 'I').replace(/î/g, 'i')
			.replace(/Ê/g, 'E').replace(/ê/g, 'e')
			.replace(/Ô/g, 'O').replace(/ô/g, 'o')
			// Unicode NFD ile geri kalan tüm aksan karakterlerini düşür (è, é, ñ vb.)
			.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
			.toUpperCase();
	};

	const eventCounts: any = {}
	if (!filters.city && !!events.cityEventCounts) {
		events.cityEventCounts.forEach((city: CityEventCount) => {
			// Normalize key ile kaydet: "İstanbul" → "ISTANBUL"
			eventCounts[normalizeCity(city.city)] = {
				city: city.city,
				count: city.count,
				averageDailyCount: city.averageDailyCount,
				lastDayCount: city.lastDayCount,
				logThreatLevel: city.logThreatLevel,
				threatLevel: city.threatLevel,
				t: calculateTValue(city.count),
				tInterpretation: interpretTValue(calculateTValue(city.count)),
				events: events.events?.items?.filter((item: any) => item.city === city.city).map((item: any) => ({
					latitude: item.latitude,
					longitude: item.longitude,
					eventTypeId: item.eventType.id,
					description: item.description,
					date: item.date
				}))
			}
		});
	} else if (filters.city && !!events.districtEventCounts) {
		events.districtEventCounts.forEach((district: DistrictEventCount) => {
			eventCounts[district.district] = {
				city: district.district,
				count: district.count,
				averageDailyCount: district.averageDailyCount,
				lastDayCount: district.lastDayCount,
				logThreatLevel: district.logThreatLevel,
				threatLevel: district.threatLevel,
				t: calculateTValue(district.count),
				tInterpretation: interpretTValue(calculateTValue(district.count)),
				events: events.events?.items?.filter((item: any) => item.district === district.district).map((item: any) => ({
					latitude: item.latitude,
					longitude: item.longitude,
					eventTypeId: item.eventType.id,
					description: item.description,
					date: item.date
				}))
			}
		});
	}

	const CityBounds = () => {
		map.setView(center, isTabletScreen ? 5 : 6)

		// Marker'ları temizle
		map.eachLayer((layer) => {
			if (layer instanceof L.MarkerClusterGroup) {
				map.removeLayer(layer);
			}
		});

		return ilSinirlari.features.map((feature: any) => {
			const city = feature.properties.name;
			// Normalize ile ara: "Istanbul" → "ISTANBUL" = "İstanbul" normalize edilmişi
			const eventData = eventCounts[normalizeCity(city)];
			const uniqueKey = feature.id || `city-${city}`;

			return (
				<GeoJSON
					key={uniqueKey}
					data={feature}
					style={getFeatureStyle(eventData, filters.eventTypeId, useNewColorScale)}
					onEachFeature={(feature: any, layer: any) => {
						layer.on({
							click: () => {
								const bounds = layer.getBounds();
								map.fitBounds(bounds, { padding: [50, 50] });
								setFilters({ ...filters, city });
							}
						});
						addFeatureEvents(layer, eventData)
					}}
				/>
			);
		})
	}

	// İl seçildiğinde ilçe sınırlarını yükle
	useEffect(() => {
		if (filters.city) {
			fetch('/simplified_ilce_sinirlari.json')
				.then(response => response.json())
				.then(data => {
					setDistrictBounds(data);
				})
				.catch(error => {
					console.error('İlçe sınırları yüklenirken hata:', error);
				});
		}
	}, [filters.city]);

	const DistrictBounds = () => {
		// İlçe sınırları ve harita işlemleri için memoized callback
		const updateMap = React.useCallback(() => {
			if (districtBounds?.features) {
				const ilFeature = districtBounds.features.find((q: any) => q.properties.name === filters.city);

				if (ilFeature) {
					const layer = L.geoJSON(ilFeature);
					const newBounds = layer.getBounds();
					map.fitBounds(newBounds);
				}

				// Tüm katmanları temizle
				map.eachLayer((layer) => {
					if (layer instanceof L.MarkerClusterGroup) {
						map.removeLayer(layer);
					}
				});

				if (events?.events?.items?.length > 0 && showPieChart) {
					const mappedItems = events.events.items.map((item: {
						latitude: any;
						longitude: any;
						eventType: { id: any; };
						description?: string;
						date?: string;
					}) => ({
						latitude: item.latitude,
						longitude: item.longitude,
						eventTypeId: item.eventType.id,
						description: item.description,
						date: item.date
					}));

					markerClusterPieChart(
						map,
						mappedItems,
						(events, coordinates) => {
							setSelectedPinEvents(events);
							setSelectedPinCoordinates(coordinates);
							setEventPinModalOpen(true);
						}
					);
				}
			}
		}, [districtBounds, filters.city, map, events, showPieChart]);

		// Effect'i güncellenen callback ile çalıştır
		useEffect(() => {
			updateMap();
		}, [updateMap]);

		const getModifiedFeatureStyle = (eventData: any) => {
			const baseStyle = getFeatureStyle(eventData, filters.eventTypeId, useNewColorScale);
			return (feature: any) => {
				const style = baseStyle(feature);
				return showDistrictFill ? style : { ...style, fillOpacity: 0 };
			};
		};

		// İlçe sınırlarını render et
		return (
			<React.Fragment>
				{districtBounds?.features
					.filter((q: any) => q.properties.IL_ADI === filters.city)
					.map((feature: any) => {
						const eventData = eventCounts[feature.properties.ILCE_ADI];
						const uniqueKey = `district-${feature.id || feature.properties.ILCE_ADI}`;

						return (
							<GeoJSON
								pane="tilePane"
								key={uniqueKey}
								data={feature}
								style={getModifiedFeatureStyle(eventData)}
								onEachFeature={(feature, layer) => {
									addFeatureEvents(layer, eventData);
									layer.on('click', () => {
										setSelectedDistrict(feature.properties.ILCE_ADI);
										setDistrictModalOpen(true);
									});
								}}
							/>
						);
					})}
				<DistrictModal
					open={districtModalOpen}
					onClose={() => setDistrictModalOpen(false)}
					districtName={selectedDistrict}
					filters={filters}
				/>
				{selectedPinCoordinates && (
					<EventPinModal
						open={eventPinModalOpen}
						onClose={() => setEventPinModalOpen(false)}
						events={selectedPinEvents}
						coordinates={selectedPinCoordinates}
					/>
				)}
			</React.Fragment>
		);
	}

	// Koordinat kontrolü için yardımcı fonksiyon
	const isValidCoordinates = (input: string): boolean => {
		const coordPattern = /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/;
		if (!coordPattern.test(input)) return false;

		const [lat, lng] = input.split(',').map(coord => parseFloat(coord.trim()));
		return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
	};

	// Koordinat araması için yeni fonksiyon
	const handleCoordinateSearch = (input: string) => {
		const [lat, lng] = input.split(',').map(coord => parseFloat(coord.trim()));
		const newPosition: [number, number] = [lat, lng];
		setSearchPin(newPosition);

		// Wikipedia verisi için temiz başlık oluştur
		const cleanTitle = `Koordinat: ${lat.toFixed(6)}, ${lng.toFixed(6)}`;

		setLoading(true);
		setWikipediaData({
			title: cleanTitle,
			summary: null,
			thumbnail: null
		});

		// Hangi ilin sınırları içinde olduğunu bul
		const selectedCity = ilSinirlari.features.find((feature: any) =>
			L.geoJSON(feature).getBounds().contains(L.latLng(newPosition))
		);

		if (selectedCity) {
			// İlin sınırlarını yükle
			const cityName = selectedCity.properties.name;
			setFilters({ ...filters, city: cityName });

			// Şehir sınırlarına zoom yap
			const bounds = L.geoJSON(selectedCity).getBounds();
			map.fitBounds(bounds);
		} else {
			console.warn("Bu konum herhangi bir şehir sınırları içinde değil.");
		}

		// Direkt olarak ReportPinModal'ı aç
		setReportPinModalOpen(true);
		setSuggestions([]);
		setLoading(false);
	};

	// handleSearch fonksiyonunu güncelleyelim
	const handleSearch = async () => {
		if (!searchQuery) return;

		// Koordinat kontrolü
		if (isValidCoordinates(searchQuery)) {
			handleCoordinateSearch(searchQuery);
			return;
		}

		try {
			const response = await fetch(
				`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
					searchQuery
				)}&format=json&addressdetails=1&accept-language=tr`
			);
			const data = await response.json();
			setSuggestions(data);
		} catch (error) {
			console.error('Error fetching data:', error);
		}
	};

	const [showClearButton, setShowClearButton] = useState(false);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			handleSearch();
		}
	};

	// Handler fonksiyonları
	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSearchQuery(value);
		setShowClearButton(value.length > 0);

		// Eğer input boş ise suggestion listesini temizle
		if (!value) {
			setSuggestions([]);
		}
	};

	const handleClear = () => {
		setSearchQuery('');
		setShowClearButton(false);
		setSuggestions([]);
	};


	// Öneri seçimi
	const handleSuggestionSelect = async (suggestion: any) => {
		const { lat, lon, display_name } = suggestion;
		const newPosition: [number, number] = [parseFloat(lat), parseFloat(lon)];
		setSearchPin(newPosition);

		const cleanTitle = getCleanTitle(display_name);

		setLoading(true);
		if (cleanTitle) {
			try {
				const wikiData = await fetchWikipediaData(cleanTitle);
				if (!wikiData) {
					if (!!lat && !!lon) {
						const coordData = await fetchWikipediaDataByCoordinates(lat, lon);
						if (coordData) {
							setWikipediaData({
								title: coordData.title,
								summary: coordData.extract,
								thumbnail: coordData.thumbnail?.source || null
							});
						}
					}
				} else {
					setWikipediaData({
						title: wikiData.title,
						summary: wikiData.extract,
						thumbnail: wikiData.thumbnail?.source || null
					});
				}
				// Yeni pin için savedPinData'yı null yap
				setSavedPinData(null);
				// Direkt olarak ReportPinModal'ı aç
				setReportPinModalOpen(true);
				setSuggestions([]);
			} catch (error) {
				console.error('Wikipedia verisi alınırken hata:', error);
			}
		} else {
			setWikipediaData({
				title: null,
				summary: null,
				thumbnail: null
			});
			// Wikipedia verisi olmasa bile ReportPinModal'ı aç
			setReportPinModalOpen(true);
		}
		setLoading(false);

		// Hangi ilin sınırları içinde olduğunu bul
		const selectedCity = ilSinirlari.features.find((feature: any) =>
			L.geoJSON(feature).getBounds().contains(L.latLng(newPosition))
		);

		if (selectedCity) {
			// İlin sınırlarını yükle
			const cityName = selectedCity.properties.name;
			setFilters({ ...filters, city: cityName });

			// Şehir sınırlarına zoom yap
			const bounds = L.geoJSON(selectedCity).getBounds();
			map.fitBounds(bounds);
		} else {
			console.warn("Bu konum herhangi bir şehir sınırları içinde değil.");
		}

		setLoading(false);
	};

	// handleSearchPinClick fonksiyonunu basitleştirelim
	const handleSearchPinClick = () => {
		if (searchPin) {
			// Sadece modal'ı aç, Wikipedia verisi zaten var
			setWikipediaModalOpen(true);
		}
	};

	// Pin tıklama işlemi - reports sayfasına yönlendirme
	const handlePinClick = (pin: any) => {
		console.log('📍 Pin tıklandı:', pin);
		// Reports sayfasına yönlendirme yap
		window.location.href = `/reports/${pin.id}`;
	};

	// useEffect içindeki draw control kısmını güncelleyelim
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (!map) return;

		drawnItems.addTo(map);

		// Sadece il seçili ise draw control'ü ekle
		if (filters.city) {
			const drawControl = new L.Control.Draw({
				draw: {
					polyline: false,
					rectangle: false,
					marker: false,
					circlemarker: false,
					polygon: false,
					circle: {
						showRadius: true,
						metric: true,
						feet: false,
						shapeOptions: {
							color: '#3388ff'
						}
					}
				}
			});

			map.addControl(drawControl);

			// Erişilebilirlik özelliklerini manuel olarak ekle
			setTimeout(() => {
				const drawButton = document.querySelector('.leaflet-draw-draw-circle');
				if (drawButton) {
					drawButton.setAttribute('aria-label', 'Daire çizme aracı');
					drawButton.setAttribute('role', 'button');
					drawButton.setAttribute('tabindex', '0');
					// Klavye erişilebilirliği için
					(drawButton as HTMLElement).addEventListener('keydown', (e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							(drawButton as HTMLElement).click();
						}
					});
				}
			}, 100);

			map.on(L.Draw.Event.CREATED, (e: any) => {
				const layer = e.layer;
				const center = layer.getLatLng();
				const radius = layer.getRadius();

				// Çizilen daire bilgilerini sakla
				setDrawnCircle({
					center: [center.lat, center.lng],
					radius: radius
				});

				// Çizilen daireyi haritaya ekle
				drawnItems.addLayer(layer);

				// Merkez noktasına marker ekle
				setSearchPin([center.lat, center.lng]);

				setWikipediaData({
					title: null,
					summary: null,
					thumbnail: null
				});

				// Wikipedia modalı açmak yerine direkt ReportPinModal'ı aç
				setReportPinModalOpen(true);
			});

			// Cleanup fonksiyonunu güncelle
			return () => {
				const drawButton = document.querySelector('.leaflet-draw-draw-circle');
				if (drawButton) {
					(drawButton as HTMLElement).removeEventListener('keydown', () => { });
				}
				map.removeControl(drawControl);
				map.off(L.Draw.Event.CREATED);
				drawnItems.clearLayers();
			};
		} else {
			// İl seçili değilse sadece drawnItems'ı temizle
			return () => {
				drawnItems.clearLayers();
			};
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map, filters.city]); // drawnItems'ı dependency array'den çıkarık

	// Pin'in seçili şehir sınırları içinde olup olmadığını kontrol et
	const isPinInSelectedCity = (pin: any): boolean => {
		if (!filters.city) {
			return false;
		}

		// İlce sınırları yerine ilSinirlari kullan (bu her zaman yüklü)
		if (!ilSinirlari) {
			return false;
		}

		// İl sınırlarını bul - ilSinirlari kullan
		const selectedCityFeature = ilSinirlari.features.find(
			(feature: any) => feature.properties.name === filters.city
		);

		if (!selectedCityFeature) {
			return false;
		}

		// Pin koordinatlarını kontrol et
		const point = L.latLng(pin.latitude, pin.longitude);
		const cityPolygon = L.geoJSON(selectedCityFeature);
		const cityBounds = cityPolygon.getBounds();
		const isInCity = cityBounds.contains(point);

		return isInCity;
	};

	// Pin'leri localStorage'dan yüklemek için fonksiyon
	const loadPinsFromLocalStorage = () => {
		try {
			const storedPins = localStorage.getItem('pins');

			if (storedPins) {
				const parsedPins = JSON.parse(storedPins);

				// Eğer parsedPins bir array ise doğrudan kullan, değilse data, items property'si var mı kontrol et
				const pinsArray = Array.isArray(parsedPins) ? parsedPins : (parsedPins.data || parsedPins.items || []);

				if (pinsArray.length > 0) {
					// İl sınırları içindeki pinleri filtrele
					const filteredPins = pinsArray.filter((pin: any) => isPinInSelectedCity(pin));
					setCityPins(filteredPins);
				} else {
					setCityPins([]);
				}
			} else {
				setCityPins([]);
			}
		} catch (error) {
			console.error('localStorage\'dan pinler okunurken hata:', error);
			setCityPins([]);
		}
	};

	// İl sınırları değiştiğinde
	useEffect(() => {
		if (ilce_sinirlari) {
			// İl sınırları yüklendi
		}
	}, [ilce_sinirlari]);

	// filters.city değiştiğinde pin'leri yükle
	useEffect(() => {
		if (filters.city) {
			// ilce_sinirlari'nı beklemeden ilSinirlari ile işlem yap
			loadPinsFromLocalStorage();
		} else {
			// İl seçili değilse pinleri temizle
			setCityPins([]);
		}
	}, [filters.city]); // ilce_sinirlari'nı dependency'den çıkar



	// handleReportPinSubmit fonksiyonunu güncelle
	const handleReportPinSubmit = async (reportPinData: any) => {
		setSavedPinData({
			id: reportPinData.id,
			name: reportPinData.name,
			latitude: searchPin![0],
			longitude: searchPin![1],
			radius: drawnCircle?.radius || reportPinData.radius,
			sector: reportPinData.sector
		});

		setReportPinModalOpen(false);
		setSearchPin(null);
		setDrawnCircle(null);
		drawnItems.clearLayers();

		// Pin'leri yeniden yükle
		loadPinsFromLocalStorage();
	};

	// handleWikipediaModalClose fonksiyonunu güncelleyelim
	const handleWikipediaModalClose = () => {
		setWikipediaModalOpen(false);
		setWikipediaData({
			title: null,
			summary: null,
			thumbnail: null
		});

		// Eğer kaydedilmemiş bir pin varsa ve ReportPinModal açık değilse
		if (searchPin && !savedPinData?.id && !reportPinModalOpen) {
			setReportPinModalOpen(true);
		} else if (!savedPinData?.id) {
			setSavedPinData(null);
			setSearchPin(null);
			setDrawnCircle(null);
			drawnItems.clearLayers();
		}
	};

	// Filters değiştiğinde veya temizlendiğinde markerları temizle
	useEffect(() => {
		// Sadece markerCluster ve heat layer'ları temizle, custom pinleri koru
		map.eachLayer((layer) => {
			// MarkerClusterGroup veya heat layer ise kaldır
			if (layer instanceof L.MarkerClusterGroup ||
				// @ts-ignore - HeatLayer kontrolü için
				layer._heat) {
				map.removeLayer(layer);
			}
			// Custom marker'ları kontrol et ve koru
			if (layer instanceof L.Marker) {
				// Eğer marker customMarkerIcon kullanıyorsa (yani custom pin ise) koru
				const markerIcon = (layer.getIcon() as L.Icon);
				if (markerIcon.options.iconUrl !== customMarkerIcon.options.iconUrl) {
					map.removeLayer(layer);
				}
			}
		});

		// Eğer şehir seçili değilse veya events yoksa, marker'ları ekleme
		if (!filters.city || !events?.events?.items) {
			return;
		}

		// Şehir seçili ve events varsa
		if (events?.events?.items?.length > 0) {
			if (showPieChart) {
				const mappedItems = events.events.items.map((item: {
					latitude: any;
					longitude: any;
					eventType: { id: any; };
					description?: string;
					date?: string;
				}) => ({
					latitude: item.latitude,
					longitude: item.longitude,
					eventTypeId: item.eventType.id,
					description: item.description,
					date: item.date
				}));

				markerClusterPieChart(
					map,
					mappedItems,
					(events, coordinates) => {
						setSelectedPinEvents(events);
						setSelectedPinCoordinates(coordinates);
						setEventPinModalOpen(true);
					}
				);
			} else if (showHeatmap) {
				const heatData = events.events.items.map((item: any) => [
					item.latitude,
					item.longitude,
					1
				]);

				// @ts-ignore
				const heatLayer = L.heatLayer(heatData, {
					radius: 35,
					blur: 30,
					maxZoom: 7,
					max: 1,
					gradient: {
						0.4: 'blue',
						0.6: 'lime',
						0.8: 'yellow',
						1.0: 'red'
					}
				}).addTo(map);

				// @ts-ignore
				map.heatLayer = heatLayer;
			}
		}
	}, [filters, events, showPieChart, showHeatmap, map]);

	// Pin silme işlemi - önbelleği de temizle
	const handleDeletePin = (pinId: string) => {
		setCityPins((prevPins) => prevPins.filter(pin => pin.id !== pinId));
		setSearchPin(null);
		setWikipediaModalOpen(false);

		// Silinen pine ait önbelleği temizle
		CacheService.clear(MAP_CACHE_KEYS.PIN_INFO(pinId));
		CacheService.clear(MAP_CACHE_KEYS.PINS); // Tüm pin listesini de güncelle
	};

	// Switch handler'ları geri ekleyelim
	const handlePieChartSwitch = (checked: boolean) => {
		setShowPieChart(checked);
		if (checked) {
			if (events?.events?.items && events.events.items.length > 0) {
				const mappedItems = events.events.items.map((item: {
					latitude: any;
					longitude: any;
					eventType: { id: any; };
					description?: string;
					date?: string;
				}) => ({
					latitude: item.latitude,
					longitude: item.longitude,
					eventTypeId: item.eventType.id,
					description: item.description,
					date: item.date
				}));

				markerClusterPieChart(map, mappedItems);
			}
		} else {
			map.eachLayer((layer) => {
				if (layer instanceof L.MarkerClusterGroup) {
					map.removeLayer(layer);
				}
			});
		}
	};

	const handleDistrictFillSwitch = (checked: boolean) => {
		setShowDistrictFill(checked);
	};

	const handleHeatmapSwitch = (checked: boolean) => {
		setShowHeatmap(checked);
		if (checked) {
			if (showPieChart) {
				setShowPieChart(false);
				map.eachLayer((layer) => {
					if (layer instanceof L.MarkerClusterGroup) {
						map.removeLayer(layer);
					}
				});
			}

			if (events?.events?.items && events.events.items.length > 0) {
				const heatData = events.events.items.map((item: any) => [
					item.latitude,
					item.longitude,
					1
				]);

				// @ts-ignore
				const heatLayer = L.heatLayer(heatData, {
					radius: 25,
					blur: 15,
					maxZoom: 10,
					max: 1,
					gradient: {
						0.4: 'blue',
						0.6: 'lime',
						0.8: 'yellow',
						1.0: 'red'
					}
				}).addTo(map);

				// @ts-ignore
				map.heatLayer = heatLayer;
			}
		} else {
			// @ts-ignore
			if (map.heatLayer) {
				// @ts-ignore
				map.removeLayer(map.heatLayer);
				// @ts-ignore
				map.heatLayer = null;
			}
		}
	};

	return (
		<React.Fragment>
			<TileLayer
				url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
				attribution=""
				detectRetina={true}
				opacity={1}
				noWrap={true}
				keepBuffer={2}
				updateWhenIdle={false}
				updateWhenZooming={true}
				updateInterval={200}
			/>
			{!isLoading && (
				<>
					<Legend
						selectedEventType={filters.eventTypeId}
						useNewColorScale={useNewColorScale}
						onColorScaleChange={setUseNewColorScale}
					/>
					{filters.city && (
						<div
							style={{
								position: 'absolute',
								top: '10px',
								left: '10px',
								zIndex: 1000,
								backgroundColor: 'white',
								padding: window.innerWidth < 900 ? '8px' : '10px',
								borderRadius: '8px',
								boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
								fontSize: window.innerWidth < 900 ? '0.8rem' : '1rem',
							}}
						>
							<FormControlLabel
								control={
									<Switch
										checked={showDistrictFill}
										onChange={(e) => handleDistrictFillSwitch(e.target.checked)}
										disabled={showHeatmap}
										color="primary"
									/>
								}
								label="İlçe Renklendirme"
							/>
							<FormControlLabel
								control={
									<Switch
										checked={showPieChart}
										onChange={(e) => {
											if (e.target.checked && showHeatmap) {
												setShowHeatmap(false);
											}
											handlePieChartSwitch(e.target.checked);
										}}
										disabled={showHeatmap}
										color="primary"
									/>
								}
								label="Olay Dağılımı"
							/>
							<FormControlLabel
								control={
									<Switch
										checked={showHeatmap}
										onChange={(e) => {
											if (e.target.checked) {
												setShowPieChart(false);
												setShowDistrictFill(false);
											}
											handleHeatmapSwitch(e.target.checked);
										}}
										color="primary"
									/>
								}
								label="Isı Haritası"
							/>
						</div>
					)}
					<div
						style={{
							position: 'absolute',
							top: '10px',
							right: '10px',
							zIndex: 1000,
							backgroundColor: 'white',
							padding: window.innerWidth < 900 ? '8px' : '10px',
							borderRadius: '8px',
							boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
							width: isTabletScreen ? '160px' : (window.innerWidth < 900 ? '180px' : '225px'),
						}}
					>
						{/* Arama Kutusu */}
						<div style={{ position: 'relative', display: 'flex', gap: '5px' }}>
							<div style={{ flex: 1, position: 'relative' }}>
								<input
									type="text"
									value={searchQuery}
									onChange={handleInputChange}
									onKeyDown={handleKeyDown}
									placeholder="Yer adı veya koordinat"
									style={{
										width: '100%',
										padding: '5px',
										paddingRight: '25px',
										fontSize: '14px'
									}}
									title="Yer adı girebilir veya koordinat girebilirsiniz (enlem,boylam)"
								/>
								{showClearButton && (
									<div
										onClick={handleClear}
										style={{
											position: 'absolute',
											right: '8px',
											top: '50%',
											transform: 'translateY(-50%)',
											cursor: 'pointer',
											opacity: 0.5,
											fontSize: '16px',
											color: '#666'
										}}
									>
										×
									</div>
								)}
							</div>
							<button
								onClick={handleSearch}
								onMouseOver={(e) => {
									(e.target as HTMLButtonElement).style.backgroundColor = '#1565c0';
								}}
								onMouseOut={(e) => {
									(e.target as HTMLButtonElement).style.backgroundColor = '#1976d2';
								}}
								style={{
									padding: '5px 10px',
									backgroundColor: '#1976d2',
									color: 'white',
									border: 'none',
									borderRadius: '4px',
									cursor: 'pointer',
									fontSize: '14px',
									transition: 'background-color 0.2s'
								}}
								title="Ara"
							>
								Ara
							</button>
						</div>
						{/* Koordinat formatı için yardımcı metin */}
						{searchQuery && !isValidCoordinates(searchQuery) && searchQuery.includes(',') && (
							<div style={{
								fontSize: '12px',
								color: '#ff4444',
								marginTop: '5px',
								padding: '2px 5px',
								backgroundColor: '#fff',
								borderRadius: '4px'
							}}>
								Geçersiz koordinat formatı. Örnek: 32.322,14.323
							</div>
						)}
						{/* Öneri Listesi - searchQuery kontrolü eklendi */}
						{searchQuery && suggestions.length > 0 && (
							<ul
								style={{
									listStyleType: 'none',
									padding: 0,
									margin: '10px 0 0 0',
									backgroundColor: 'white',
									border: '1px solid #ddd',
									borderRadius: '4px',
									boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
									maxHeight: '600px',
									overflowY: 'auto',
									position: 'absolute',
									width: '100%',
									zIndex: 1000,
								}}
							>
								{suggestions.map((suggestion, index) => (
									<li
										key={index}
										style={{
											padding: '8px',
											cursor: 'pointer',
											borderBottom: '1px solid #eee',
										}}
										onClick={() => handleSuggestionSelect(suggestion)}
									>
										{suggestion.display_name}
									</li>
								))}
							</ul>
						)}
					</div>

					{/* Search Pin */}
					{searchPin && (
						<Marker
							position={searchPin}
							icon={customMarkerIcon}
							eventHandlers={{
								click: handleSearchPinClick, // Pin'e tıklayınca modal açılır
							}}
						/>
					)}

					{/* Modal */}
					<WikipediaModal
						open={wikipediaModalOpen}
						onClose={handleWikipediaModalClose}
						onDelete={() => handleDeletePin(savedPinData?.id || '')}
						onCreateReport={() => setReportPinModalOpen(true)}
						onOpenChecklist={() => setChecklistOpen(true)}
						title={wikipediaData.title}
						loading={loading}
						summary={wikipediaData.summary}
						savedPinData={savedPinData}
					/>

					<ReportPinModal
						open={reportPinModalOpen}
						onClose={() => {
							setReportPinModalOpen(false);
							// Pin'i ve çizilen daireyi haritadan kaldır
							setSearchPin(null);
							setDrawnCircle(null);
							drawnItems.clearLayers();
						}}
						coordinates={searchPin ? { latitude: searchPin[0], longitude: searchPin[1] } : undefined}
						radius={drawnCircle?.radius}
						wikipediaData={{
							title: wikipediaData.title,
							summary: wikipediaData.summary
						}}
						onSubmit={handleReportPinSubmit}
					/>

					<ChecklistModal
						open={checklistOpen}
						onClose={() => {
							setChecklistOpen(false);
						}}
						pinId={savedPinData?.id || ''}
					/>

					{!filters.city ? <CityBounds /> : <DistrictBounds />}

					{/* Şehir pinlerini göster - Sadece il seçiliyse */}
					{filters.city && cityPins.map(pin => (
						<Marker
							key={pin.id}
							position={[pin.latitude, pin.longitude]}
							icon={customMarkerIcon}
							eventHandlers={{
								click: () => handlePinClick(pin)
							}}
						/>
					))}
				</>
			)}
		</React.Fragment>
	)
}

export default Map;